import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Printer, 
  ChevronRight, 
  User, 
  UserPlus,
  Shield, 
  Crown, 
  DollarSign, 
  AlertCircle, 
  CheckCircle, 
  HelpCircle, 
  ArrowRight,
  Clock,
  Layers,
  Percent,
  FileSpreadsheet,
  FileText,
  ToggleLeft,
  ToggleRight,
  Sparkles,
  ShieldCheck,
  Info
} from 'lucide-react';
import { motion } from 'motion/react';
import { InteractiveWalkthrough } from './InteractiveWalkthrough';
import { useFirebase } from '../contexts/FirebaseContext';
import { getSettings } from '../services/settingsService';

type RoleTab = 'employee' | 'manager' | 'admin' | 'tour';

export const UserGuide: React.FC = () => {
  const { role, user, tenantId } = useFirebase();
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@growezy.ai';
  const [requireIRS8PercentCompliance, setRequireIRS8PercentCompliance] = useState<boolean>(true);
  const [showWalkthrough, setShowWalkthrough] = useState<boolean>(false);
  
  // Default to user's current role if possible, otherwise 'employee'
  const getInitialTab = (): RoleTab => {
    if (role === 'admin') return 'admin';
    if (role === 'manager') return 'manager';
    return 'employee';
  };

  const [activeTab, setActiveTab] = useState<RoleTab>(getInitialTab());

  useEffect(() => {
    if (tenantId) {
      getSettings(tenantId).then(settings => {
        if (settings && settings.requireIRS8PercentCompliance !== undefined) {
          setRequireIRS8PercentCompliance(settings.requireIRS8PercentCompliance);
        }
      });
    }
  }, [tenantId]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-4 md:p-6">
      {/* Header section (Hidden during print via css print classes) */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-100 pb-6 no-print">
        <div>
          <div className="flex items-center gap-2 text-blue-600 mb-1.5">
            <BookOpen size={20} className="stroke-[2.5]" />
            <span className="text-xs font-black uppercase tracking-widest">Documentation & Learning</span>
          </div>
          <h1 className="text-3xl font-black text-gray-950 tracking-tight">TipValue AI User Guide & Tour</h1>
          <p className="text-gray-500 text-sm mt-1">
            Learn how to navigate and make the most of the TipValue tip management and allocation system.
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all cursor-pointer min-h-[44px]"
          >
            <Printer size={16} className="text-gray-550" />
            <span>Print User Guide</span>
          </button>
        </div>
      </div>

      {/* Print-Only Header (Only visible when printing) */}
      <div className="hidden print:block border-b-2 border-gray-800 pb-6 mb-8">
        <h1 className="text-4xl font-black text-gray-950 tracking-tight text-center">TipValue AI</h1>
        <h2 className="text-2xl font-bold text-gray-700 text-center mt-1">Complete System User Guide & Handbook</h2>
        <p className="text-gray-500 text-xs text-center mt-2">
          Document generated on {new Date().toLocaleDateString()} for GrowEzy.ai Platform Tenants.
        </p>
      </div>

        {/* Role Tabs Navigation (Hidden during print) */}
        <div className="flex flex-wrap gap-2 p-1.5 bg-gray-100/80 rounded-2xl no-print">
          <button
            onClick={() => setActiveTab('tour')}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex-1 md:flex-none justify-center cursor-pointer ${
              activeTab === 'tour'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/40'
            }`}
          >
            <Sparkles size={15} />
            <span>Interactive Tour</span>
          </button>

          <button
            onClick={() => setActiveTab('employee')}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex-1 md:flex-none justify-center cursor-pointer ${
              activeTab === 'employee'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/40'
            }`}
          >
            <User size={15} />
            <span>Employee Guide</span>
          </button>
          
          <button
            onClick={() => setActiveTab('manager')}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex-1 md:flex-none justify-center cursor-pointer ${
              activeTab === 'manager'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/40'
            }`}
          >
            <Shield size={15} />
            <span>Manager Guide</span>
          </button>

          <button
            onClick={() => setActiveTab('admin')}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex-1 md:flex-none justify-center cursor-pointer ${
              activeTab === 'admin'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/40'
            }`}
          >
            <Shield size={15} className="text-purple-500" />
            <span>Admin Guide</span>
          </button>
        </div>

      {/* Main Guide Content (Shows active tab on screen, shows ALL sections when printing so they get a complete manual) */}
      <div className="space-y-12">
        
        {/* ==================== TOUR SECTION ==================== */}
        <section className={`${activeTab === 'tour' ? 'block' : 'hidden'} space-y-6`}>
           <InteractiveWalkthrough onClose={() => setActiveTab('employee')} />
        </section>

        {/* ==================== 1. EMPLOYEE SECTION ==================== */}
        <section className={`${activeTab === 'employee' ? 'block' : 'hidden'} print:block space-y-6 page-break-after`}>
          <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 print:bg-transparent print:border print:border-gray-300">
              <User size={20} className="stroke-[2.5]" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Part I</span>
              <h2 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight">The Employee Handbook</h2>
            </div>
          </div>
          
          <p className="text-gray-600 leading-relaxed text-sm">
            Welcome to TipValue AI! As a team member, you have a personal, secure portal to track your hard-earned tips, submit new cash/jar earnings, and resolve discrepancies quickly and transparently.
          </p>

          {/* Understanding Allocation Period Date Ranges */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/70 rounded-[2rem] p-6 shadow-sm">
            <div className="flex gap-5 items-start">
              <div className="p-3 bg-blue-100 text-blue-800 rounded-2xl shrink-0">
                <Info size={22} className="stroke-[2.5]" />
              </div>
              <div>
                <h4 className="text-sm font-black text-blue-900 uppercase tracking-wider">Understanding Allocation Period Date Ranges</h4>
                <p className="text-xs text-blue-700 font-medium leading-relaxed mt-1">
                  A guide to how we align tip distributions with your payroll.
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-4 mt-4 border-t border-blue-100/30">
              <p className="text-xs text-blue-700 font-medium leading-relaxed">
                To ensure complete alignment with payroll, your tip allocations are dynamically grouped using the <strong className="text-blue-950">same defined pay periods</strong> configured in your organization's payroll setup. This ensures perfect sync between your verified tip records and your final paycheck.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/60 p-4 rounded-2xl border border-blue-100/50 space-y-1">
                  <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest block mb-1">📅 Automated Payroll Alignment</span>
                  <p className="text-[11px] text-blue-800 font-medium leading-relaxed">
                    By grouping tip allocations directly into your organization's official payroll date ranges, the exported tip payroll summary matches your signed off records exactly.
                  </p>
                </div>
                <div className="bg-white/60 p-4 rounded-2xl border border-blue-100/50 space-y-1">
                  <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest block mb-1">⚖️ IRS Regulatory Standard</span>
                  <p className="text-[11px] text-blue-800 font-medium leading-relaxed">
                    IRS federal regulations require regular examination and reporting of tip earnings. This systematic sign-off creates a legally compliant audit trail for you and your employer.
                  </p>
                </div>
                <div className="bg-white/60 p-4 rounded-2xl border border-blue-100/50 space-y-1">
                  <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest block mb-1">🔒 Certified Ledger Locking</span>
                  <p className="text-[11px] text-blue-800 font-medium leading-relaxed">
                    Your digital signature locks the entire pay period in the verified ledger. Once signed off, these transactions are secured from changes or subsequent editing by management.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Record Integrity & Data Lineage */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/70 rounded-[2rem] p-6 shadow-sm">
            <div className="flex gap-5 items-start">
              <div className="p-3 bg-blue-100 text-blue-800 rounded-2xl shrink-0">
                <ShieldCheck size={22} className="stroke-[2.5]" />
              </div>
              <div>
                <h4 className="text-sm font-black text-blue-900 uppercase tracking-wider">Record Integrity & Data Lineage</h4>
                <p className="text-xs text-blue-700 font-medium leading-relaxed mt-1">
                  TipValue AI is built with transparency and reliability at its core.
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-4 mt-4 border-t border-blue-100/30">
              <p className="text-xs text-blue-700 font-medium leading-relaxed">
                Our platform ensures that every tip transaction is accurately recorded, traceable, and secure.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/60 p-4 rounded-2xl border border-blue-100/50 space-y-1">
                  <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest block mb-1">🔗 Traceability</span>
                  <p className="text-[11px] text-blue-800 font-medium leading-relaxed">
                    Every allocated tip item points directly back to its origin. This allows you to verify that total tip pools were allocated accurately.
                  </p>
                </div>
                <div className="bg-white/60 p-4 rounded-2xl border border-blue-100/50 space-y-1">
                  <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest block mb-1">🔒 Permanent Records</span>
                  <p className="text-[11px] text-blue-800 font-medium leading-relaxed">
                    Once you sign off on a payroll period, your approval is saved, creating a reliable history of your confirmed earnings.
                  </p>
                </div>
                <div className="bg-white/60 p-4 rounded-2xl border border-blue-100/50 space-y-1">
                  <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest block mb-1">📊 Calculation Consistency</span>
                  <p className="text-[11px] text-blue-800 font-medium leading-relaxed">
                    Calculations, totals, and logs are synchronized across all system features, preventing drift between your dashboard and payroll exports.
                  </p>
                </div>
                <div className="bg-white/60 p-4 rounded-2xl border border-blue-100/50 space-y-1">
                  <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest block mb-1">📜 System Accuracy & Regulatory Alignment</span>
                  <p className="text-[11px] text-blue-800 font-medium leading-relaxed">
                    Our platform helps maintain organized records, supporting your employer in keeping clear, defensible data for regulatory reporting.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* My Tips */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-emerald-500 text-xl w-6 shrink-0">01</div>
                <h3 className="font-bold text-gray-900 text-base">Tracking Your Tips ("My Tips" Dashboard)</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Once logged in, click the **Dashboard** option at the top of the left-hand navigation sidebar. This is your personal home screen.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-emerald-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Check Your Totals:</strong> At the top of your dashboard, you will see three colored stat cards showing your consolidated earnings for **POS Credit Card Tips**, **Pooled Cash Tips**, and **Jar Tips**.
                    </li>
                    <li>
                      <strong>Review Daily Shift Log:</strong> Scroll down to the **"Your Shift Allocations"** table. Each row lists a specific shift date, your work hours, your role, and the exact tip amount allocated to you.
                    </li>
                    <li>
                      <strong>Monitor Calculation Status:</strong> Look at the status badge next to each shift:
                      <ul className="list-disc pl-4 mt-1 space-y-1 text-gray-500">
                        <li><span className="font-semibold text-amber-600">Pending</span>: Tips have been calculated but are currently undergoing review. You can file a dispute if these numbers are incorrect.</li>
                        <li><span className="font-semibold text-green-600">Approved</span>: Tips are approved, locked, and finalized for your next payroll paycheck.</li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Submitting Cash Tips */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-emerald-500 text-xl w-6 shrink-0">02</div>
                <h3 className="font-bold text-gray-900 text-base">Submitting Cash Earnings ("Submit Cash Tips")</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on **"Submit Cash Tips"** in the left sidebar menu. This screen allows you to report any physical cash tips collected during your shift.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-blue-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Select Employee Name:</strong> Click the **Employee Name** dropdown box and choose your name. (If you are not an administrator, this will be pre-filled with your own name).
                    </li>
                    <li>
                      <strong>Choose Shift Date:</strong> Click the calendar field labeled **Tip Date** (defaults to today) and select the calendar date the shift occurred.
                    </li>
                    <li>
                      <strong>Select Shift Block:</strong> Click the **Shift** dropdown and choose the shift block worked (e.g. <em>"Morning"</em>, <em>"Night"</em>, or <em>"All Day"</em>).
                    </li>
                    <li>
                      <strong>Input Tip Amount:</strong> Click the field labeled **Tip Amount ($)** and type the exact dollar amount of cash collected (e.g., <code>150.00</code>).
                    </li>
                    <li>
                      <strong>Add Notes (Optional):</strong> Click the **Notes** box to write down any shift details (e.g., <em>"Understaffed shift, busy dinner rush"</em>).
                    </li>
                    <li>
                      <strong>Click Submit:</strong> Click the green **Submit Cash Tip** button at the bottom of the form. A success confirmation box will slide down, confirming your submission is logged in "Pending" status.
                    </li>
                    <li>
                      <strong>Access Your History Tab:</strong> Click on the **History** tab next to the <em>"Submit"</em> tab at the top of the card. This online log shows a comprehensive report of all your physical cash submissions, including their logged dates, shifts, reported amounts, notes, and live verification status.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Submitting Jar Tips */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-emerald-500 text-xl w-6 shrink-0">03</div>
                <h3 className="font-bold text-gray-900 text-base">Submitting Shared Jars ("Submit Jar Tips")</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on **"Submit Jar Tips"** in the left sidebar menu. Use this screen if your business operates a shared jar tipping bowl.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-indigo-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Select Submitter Name:</strong> Click the **Submitter Name** dropdown and choose your name.
                    </li>
                    <li>
                      <strong>Select Calendar Date:</strong> Click the **Tip Date** field to choose the date the jar was collected.
                    </li>
                    <li>
                      <strong>Select Shift Block:</strong> Click the **Shift** dropdown and choose the appropriate shift block.
                    </li>
                    <li>
                      <strong>Input Jar Amount:</strong> Click the field labeled **Jar Tip Amount ($)** and type the total jar sum.
                    </li>
                    <li>
                      <strong>Click Submit:</strong> Click the purple **Submit Jar Tip** button to complete the registration.
                    </li>
                    <li>
                      <strong>Review Shared History:</strong> Click on the **History** tab at the top of the form. This online log lists every recorded team jar submission chronologically, offering complete transparency into collective tip pools.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Filing a Tip Dispute */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-emerald-500 text-xl w-6 shrink-0">04</div>
                <h3 className="font-bold text-gray-900 text-base">Filing an Alignment Dispute ("Submit Disputes")</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on **"Submit Disputes"** in the left sidebar, or click the yellow **"File Dispute"** quick button located directly next to any pending allocation on your main Dashboard.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-rose-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Identify the Incorrect Allocation:</strong> If using the sidebar form, select your name from the **Employee Name** dropdown, and choose the calendar date of the incorrect shift under **Date of Shift**.
                    </li>
                    <li>
                      <strong>Specify Tip Category:</strong> Under **Tip Type**, click to select either <em>"Cash Tip"</em> or <em>"POS Tip"</em>.
                    </li>
                    <li>
                      <strong>Select Correction Type:</strong>
                      <ul className="list-disc pl-4 mt-1 space-y-0.5 text-gray-500">
                        <li>Select <strong>Submission-Correction</strong> if the raw amount submitted by the manager was entered incorrectly.</li>
                        <li>Select <strong>Allocation-Correction</strong> if the split percentage distributed to you was computed incorrectly.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Enter the Numbers:</strong> Type the number displayed in the **Original Amount ($)** field, then type what it should be in the **Corrected Amount ($)** field.
                    </li>
                    <li>
                      <strong>State the Reason:</strong> Click the **Dispute Reason** dropdown and choose a predefined cause (e.g. <em>"Hours incorrect"</em>, <em>"Wrong shift role"</em>, or <em>"Calculation discrepancy"</em>).
                    </li>
                    <li>
                      <strong>Write an Explanation:</strong> In the text box labeled **Explanation/Details**, describe the mistake clearly (e.g., <em>"I worked from 10:00 AM to 6:00 PM (8 hours), but the allocation sheet lists 5 hours."</em>).
                    </li>
                    <li>
                      <strong>Send to Managers:</strong> Click the red **Submit Dispute** button. This flags the shift on the managers' review screen instantly, displaying a warning icon, and blocks bulk-approvals until it is resolved.
                    </li>
                    <li>
                      <strong>Track Disputed History:</strong> Click on the **History** tab at the top of the "Submit Disputes" card. Here you can access your online ledger of past disputes, check whether they have been approved or rejected by management, and view any official explanation feedback.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Team Chat */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-emerald-500 text-xl w-6 shrink-0">05</div>
                <h3 className="font-bold text-gray-900 text-base">Instant Communication ("Chat" Channel)</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on **"Chat"** in the left sidebar menu. This is a private message board dedicated to resolving shifts and coordinating tip pools.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-indigo-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Read Messages:</strong> The chat log displays a running conversation timeline. Each message shows the sender's name, role, timestamp, and message bubble.
                    </li>
                    <li>
                      <strong>Write a Message:</strong> Click inside the text box at the bottom of the chat labeled **"Type a message..."** and type your text.
                    </li>
                    <li>
                      <strong>Send:</strong> Click the **Send** button on the right (or press the Enter key on your keyboard) to post your message instantly to all workspace managers and staff.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 md:p-5 text-xs text-blue-800 space-y-1">
            <h4 className="font-bold uppercase tracking-wider text-blue-900">💡 Non-Tech Tip for Employees:</h4>
            <p>
              Your personal dashboard is highly mobile-responsive! You can log in on your smartphone at the end of each shift, quickly submit your cash tips, and check your historical approvals in under a minute.
            </p>
          </div>
        </section>

        {/* ==================== 2. MANAGER SECTION ==================== */}
        <section className={`${activeTab === 'manager' ? 'block' : 'hidden'} print:block space-y-6 page-break-after`}>
          <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 print:bg-transparent print:border print:border-gray-300">
              <Shield size={20} className="stroke-[2.5]" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Part II</span>
              <h2 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight">The Manager Guide</h2>
            </div>
          </div>

          <p className="text-gray-600 leading-relaxed text-sm">
            As a Manager, your daily task is to oversee tip collections, apply split methods, address outstanding employee disputes, and approve calculated tips.
          </p>

          <div className="space-y-6">
            {/* Review Allocations Dashboard */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-blue-500 text-xl w-6 shrink-0">01</div>
                <h3 className="font-bold text-gray-900 text-base">Daily Verification & Reconciliation ("Review Allocations")</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on the **"Review Allocations"** link in your sidebar menu. This is your primary central command screen.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-blue-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Review Pending Tips:</strong> Look at the main grid. You will see columns for **Date**, **Staff Name**, **Type/Source** (POS, Cash, or Jar), **Amount**, and **Action**.
                    </li>
                    <li>
                      <strong>Individual Approvals:</strong> Click the green **Approve** button under the <em>"Action"</em> column to verify and finalize that specific line. This locks the entry and updates its state to <strong>"Approved"</strong>.
                    </li>
                    <li>
                      <strong>Reject Incorrect Entries:</strong> Click the red **Reject** button to flag an entry that has typos or was logged in error:
                      <ul className="list-disc pl-4 mt-1 space-y-1 text-gray-500">
                        <li>A pop-up modal box will slide into view.</li>
                        <li>Type a detailed reason in the **Rejection Reason** input box (e.g., <em>"Duplicate entry logged in error"</em>).</li>
                        <li>Click the red **Confirm Rejection** button. This wipes the incorrect entry out of the pending list.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Bulk Approval Process:</strong> At the top right, check the box labeled **Approve All Eligible Tips**. The system will scan the entire workspace and automatically move all pending shifts (that do not have any active employee disputes filed) from **"Pending"** to **"Approved"** status in one click.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Resolving Disputes */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-blue-500 text-xl w-6 shrink-0">02</div>
                <h3 className="font-bold text-gray-900 text-base">Resolving Active Employee Disputes</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Located directly underneath the main tip list on the **"Review Allocations"** screen. Any active dispute filed by an employee appears here inside a highlighted orange grid panel.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-blue-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Inspect Details:</strong> Compare the column labeled **Original Amount** with **Corrected Amount** and read the employee's detailed description in the **Details** box.
                    </li>
                    <li>
                      <strong>Resolve via Overrides:</strong> If the employee's claim is valid:
                      <ul className="list-disc pl-4 mt-1 space-y-1 text-gray-500">
                        <li>Navigate to the **My Tips** or **POS Override** log (depending on the source).</li>
                        <li>Input a manual override adjustment to correct their split amount.</li>
                        <li>Click **Approve Dispute** inside the dispute grid. This marks the dispute "Resolved" and locks in the new corrected numbers.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Reject Disputes:</strong> If the claim is verified to be incorrect after checking timesheets:
                      <ul className="list-disc pl-4 mt-1 space-y-1 text-gray-500">
                        <li>Click **Reject Dispute**.</li>
                        <li>Type the reason (e.g. <em>"Timesheet confirms punch-out was at 2:00 PM, calculation is correct."</em>).</li>
                        <li>Click **Confirm Rejection** to dismiss the dispute.</li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Shift Tip Split Methods */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-blue-500 text-xl w-6 shrink-0">03</div>
                <h3 className="font-bold text-gray-900 text-base">Verifying Tip Pool Calculations</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  Before running approval logs, ensure your calculation metrics agree with your company rules. Calculations are processed automatically based on four configured split methods:
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mt-3 pl-2 border-l-2 border-gray-250 py-1">
                  <div className="space-y-1 bg-gray-50 p-3 rounded-lg">
                    <span className="text-[11px] font-extrabold text-gray-800 flex items-center gap-1.5">
                      <Clock size={12} className="text-blue-500" /> Equal Split
                    </span>
                    <p className="text-[10px] text-gray-500 leading-normal">
                      The total tip pool is divided evenly among all participating roster members on duty. 
                      <br /><span className="italic text-gray-400">Formula: Pool / Headcount = Individual share.</span>
                    </p>
                  </div>
                  <div className="space-y-1 bg-gray-50 p-3 rounded-lg">
                    <span className="text-[11px] font-extrabold text-gray-800 flex items-center gap-1.5">
                      <Clock size={12} className="text-blue-500" /> Actual Hours Worked
                    </span>
                    <p className="text-[10px] text-gray-500 leading-normal">
                      Allocates tips proportionally based on exact hours logged on a shift.
                      <br /><span className="italic text-gray-400">Formula: (Individual Hours / Total Hours) * Pool.</span>
                    </p>
                  </div>
                  <div className="space-y-1 bg-gray-50 p-3 rounded-lg">
                    <span className="text-[11px] font-extrabold text-gray-800 flex items-center gap-1.5">
                      <Layers size={12} className="text-blue-500" /> Shift Blocks
                    </span>
                    <p className="text-[10px] text-gray-500 leading-normal">
                      Tips are isolated and split strictly among workers assigned to that shift block (e.g. AM vs PM).
                    </p>
                  </div>
                  <div className="space-y-1 bg-gray-50 p-3 rounded-lg">
                    <span className="text-[11px] font-extrabold text-gray-800 flex items-center gap-1.5">
                      <Percent size={12} className="text-blue-500" /> Roles and Points
                    </span>
                    <p className="text-[10px] text-gray-500 leading-normal">
                      Weighted splits using specific role multipliers (e.g. Server = 1.0 points, Host = 0.5 points).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Run Payroll */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-blue-500 text-xl w-6 shrink-0">04</div>
                <h3 className="font-bold text-gray-900 text-base">Payroll Cycles & Payroll Exports ("Run Payroll")</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on the **"Run Payroll"** link in your left navigation sidebar.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-blue-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Select Payroll Period:</strong> Click on the **Pay Cycle** dropdown and select your current period (e.g. Weekly, Bi-weekly, or Semi-monthly). Click on the date fields to verify start and end date ranges.
                    </li>
                    <li>
                      <strong>Confirm Approval Lock:</strong> Verify that all shift allocations within this date range have been marked as **Approved** in the Review screen.
                    </li>
                    <li>
                      <strong>Generate Payroll File:</strong> Click the blue **Run Tip Payroll Export** button. The system scans all approved shifts and consolidates cash tips, card tips, and true-up shortfall adjustments by employee.
                    </li>
                    <li>
                      <strong>Download Export:</strong> Click the green **Download CSV** button. Save this CSV spreadsheet on your computer. You can upload or import this file directly into your primary payroll provider (e.g., Gusto, ADP, Paychex) to process employee paychecks.
                    </li>
                    <li>
                      <strong>Access Export History Reports:</strong> Click on the **Export History** tab at the top of the "Run Payroll" dashboard. This online report displays a comprehensive historic registry of all previously generated payroll runs, including cycle date ranges, export timestamps, total processed tips, and employee counts.
                    </li>
                    <li>
                      <strong>Re-Download Past Archives:</strong> Click the green **Download CSV** icon next to any historic export row to instantly retrieve any previously generated spreadsheet, or view the built-in Recharts trends visualization tracking tips allocated over past pay cycles.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== 3. ADMIN SECTION ==================== */}
        <section className={`${activeTab === 'admin' ? 'block' : 'hidden'} print:block space-y-6 page-break-after`}>
          <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 print:bg-transparent print:border print:border-gray-300">
              <Shield size={20} className="stroke-[2.5]" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-purple-600">Part III</span>
              <h2 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight">The Tenant Administrator Guide</h2>
            </div>
          </div>

          <p className="text-gray-600 leading-relaxed text-sm">
            Administrators configure tenant-wide rules, connect third-party POS data providers, define role points/eligibility criteria, and output payroll logs.
          </p>

          <div className="space-y-6">
            
            {/* Staff Rostering */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-purple-500 text-xl w-6 shrink-0">01</div>
                <h3 className="font-bold text-gray-900 text-base">Staff Rostering & POS Linking</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on the **"Staff"** link in the left sidebar menu. This is where you configure your roster.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Add Team Member:</strong> Click the blue **Add Staff** button at the top right of the screen. A slide-out panel or dialog box will appear.
                    </li>
                    <li>
                      <strong>Enter Basic Details:</strong> Type the employee's full name in the **Name** field, and enter their email address in the **Email** field.
                    </li>
                    <li>
                      <strong>Assign Role:</strong> Click the **Role** dropdown and select their primary job designation (e.g. <em>"Server"</em>, <em>"Bartender"</em>, <em>"Busser"</em>, etc.). This determines their points-weight and calculation eligibility.
                    </li>
                    <li>
                      <strong>Link POS Identifier (Critical Step):</strong> Locate the field labeled **POS ID** or **POS Code**. Type the exact numeric code or username that this employee punches into your physical restaurant POS register when clocking in. This is the glue that binds raw automated transaction data to the correct profile.
                    </li>
                    <li>
                      <strong>Click Save:</strong> Click the green **Save Member** button. The employee is now linked, configured, and ready to receive tip distributions automatically.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* System Settings */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-purple-500 text-xl w-6 shrink-0">05</div>
                <h3 className="font-bold text-gray-900 text-base">System Settings & Configuration ("Settings" Dashboard)</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on the <strong>"Settings"</strong> link in the left sidebar menu.
                </p>
                <p>
                  This dashboard centralizes all administrative control over your workspace, separated into specific tabs:
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-4 mt-2 text-gray-600">
                  <div>
                    <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">General Settings</span>
                    <p>Configure your POS merchant provider, select payroll provider (e.g., ADP/Gusto), establish pay cycles, set work timezones, and toggle Form 8027 shortfall tracking for IRS compliance.</p>
                  </div>
                  <div>
                    <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">Shifts</span>
                    <p>Register your business's operational shift blocks (e.g., Morning, Dinner, Night) by defining their specific start and end times to ensure accurate daily tip pool isolation.</p>
                  </div>
                  <div>
                    <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">Staff</span>
                    <p>Manage your roster by adding team members, assigning their roles, and—most importantly—mapping their POS IDs so their physical terminal punch-ins are linked automatically to their TipValue profiles.</p>
                  </div>
                  <div>
                    <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">Allocations</span>
                    <p>Define <em>how</em> tips are split (e.g., Equal Split, by Role, or Custom Formula) and set points-weights for different roles to ensure equitable distribution based on your restaurant's specific tipping culture.</p>
                  </div>
                  <div>
                    <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">Business Profile</span>
                    <p>Maintain your legal identity, including business/DBA names, tax EIN, Merchant IDs, and primary contact information to keep your payroll logs legally accurate.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* User Inviting and Management */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-purple-500 text-xl w-6 shrink-0">03</div>
                <h3 className="font-bold text-gray-900 text-base">Inviting & Managing Users ("Users" Dashboard)</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find it:</strong> Click on the **"Users"** link in your left navigation sidebar menu.
                </p>
                <p>
                  TipValue operates as an invitation-only workspace to ensure strict payroll privacy and ledger integrity. You must explicitly invite each team member who needs credentials to access the app.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">How-To Steps:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>Open Invite Terminal:</strong> Click the blue **Invite New User** button at the top right of your Users dashboard. A dialog labeled <em>"Magic Invite Generator"</em> will open.
                    </li>
                    <li>
                      <strong>Specify Details:</strong> Enter the recipient's exact work email address under **Invite Email**.
                    </li>
                    <li>
                      <strong>Confirm Tenant ID:</strong> Double-check the **Tenant ID** field (it will automatically show your active organization's ID).
                    </li>
                    <li>
                      <strong>Assign Permission Level:</strong> Select the role they should hold inside the app from the **Invite Role** dropdown:
                      <ul className="list-disc pl-4 mt-0.5 space-y-0.5 text-gray-500">
                        <li><strong>Admin</strong>: Complete system control, setting configurations, user management, and shortfall allocation dashboards.</li>
                        <li><strong>Manager</strong>: Active tip review & verification, dispute resolution, and payroll output capabilities.</li>
                        <li><strong>Employee</strong>: Personal dashboard access, cash/jar reporting terminal, and dispute submission.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Generate Code:</strong> Click the **Generate Invite Link** button. The system securely provisions a pending token and presents a custom URL.
                    </li>
                    <li>
                      <strong>Copy Link:</strong> Click inside the text box or use the click-to-copy trigger to save the custom invite link to your clipboard.
                    </li>
                    <li>
                      <strong>Send the Email (Critical Step):</strong> 
                      <div className="bg-amber-50 border border-amber-200 text-amber-900 p-2.5 rounded-lg mt-1 font-semibold flex flex-col gap-1">
                        <span className="uppercase text-[9px] tracking-wide text-amber-800 font-extrabold">⚠️ Manual Action Required:</span>
                        <span>Because TipValue protects your workspace domain, the system <strong>does not</strong> automatically dispatch an email for you. You must open your company email client (Gmail, Outlook, etc.) and write a message, paste the copied link, and send it directly to the invited team member.</span>
                      </div>
                    </li>
                    <li>
                      <strong>Onboarding Completion:</strong> When the employee receives your email and clicks the link, they will be guided through a simple registration form to securely bind their account to your tenant.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Online Transaction Reports & POS History Logs */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="font-black text-purple-500 text-xl w-6 shrink-0">04</div>
                <h3 className="font-bold text-gray-900 text-base">Online Transaction Reports & POS History Logs</h3>
              </div>
              <div className="text-gray-500 text-xs space-y-2 leading-relaxed">
                <p>
                  <strong>Where to find them:</strong> Use the dedicated navigation links in the left sidebar menu: **"Reports"**, **"POS History"**, and **"Review Allocations"** (including the built-in **Allocation History**).
                </p>
                <p>
                  TipValue maintains secure, tamper-proof audit trails and financial summaries for all operations. This allows administrators and managers to track real-time transaction streams, raw POS credit card tip imports, manual cash logs, and past split results without guessing.
                </p>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2 mt-2 text-gray-600">
                  <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">Available Online Reports & History Dashboards:</span>
                  <ul className="list-decimal pl-4 space-y-1.5">
                    <li>
                      <strong>The Primary "Reports" Menu (Central Intelligence Dashboard):</strong> Click on the **"Reports"** link in your left navigation menu. This portal is equipped with global filtering controls at the top, allowing you to define custom **Start Date** and **End Date** filters or input keywords into the **Search by Staff** bar to find specific records. Use the 4 interactive tabs to view:
                      <ul className="list-disc pl-4 mt-1 space-y-1 text-gray-500">
                        <li><strong>Ledger Audit Trail:</strong> A chronological live stream of all transaction entries (including POS card tip allocations, employee reported cash, and jar shares) matching your active date range.</li>
                        <li><strong>Audit & Reconciliation:</strong> An interactive comparison ledger matching credit card tips imported from your POS against actual payouts. The system automatically highlights any imbalances or discrepancies.</li>
                        <li><strong>Employee Financial Overview:</strong> A compiled, filterable roster showing total cash tips, card tips, and total consolidated earnings by individual employee over the active dates.</li>
                        <li><strong>System Audit Logs:</strong> A secure, immutable history of administrative and system-wide state actions (such as settings changes, allocation finalizations, and manual adjustments) to maintain complete transparency.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>POS Upload & Reconciliation Logs ("POS History"):</strong> Click **"POS History"** in the left sidebar to access the live stream of raw uploaded CSV spreadsheets and API transmissions. This log tracks upload timestamps, matching shift blocks, total net sales, and whether the system has successfully allocated the credit card tip values to the staff.
                    </li>
                    <li>
                      <strong>Tip Allocation Auditing ("Allocation History"):</strong> On the **"Review Allocations"** page, scroll to the bottom or select the allocation ledger view to find a complete list of historical payouts. This records exactly when allocations were locked, which rule was active (Equal Split, Hours, etc.), and which administrator approved the transaction.
                    </li>
                    <li>
                      <strong>Historical Employee Cash/Jar Tracking:</strong> Admins can view individual employees' cash and jar submission logs across all past dates inside the cash history dashboards, ensuring manual entries align with physical bank deposits.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* IRS Compliance */}
            <div className="space-y-3 border-l-2 border-purple-150 pl-4 py-2 bg-purple-50/20 rounded-r-xl relative overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-black text-purple-950 text-sm md:text-base flex items-center gap-2">
                  <span className="w-1.5 h-3 bg-purple-650 rounded-xs" />
                  IRS 8% Compliance & Shortfalls (The Shortfall Ledger)
                </h3>
                
                {/* Dynamic Status Badge */}
                <div className="no-print">
                  {requireIRS8PercentCompliance ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md border border-emerald-150">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Active Compliance
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-1 rounded-md border border-gray-250">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      Feature Disabled
                    </span>
                  )}
                </div>
              </div>

              {!requireIRS8PercentCompliance && (
                <div className="bg-amber-50/75 border border-amber-200/60 p-3 rounded-xl text-xs text-amber-850 flex gap-2 items-start no-print">
                  <ToggleLeft size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Setup Note:</span> Form 8027 compliance tracking and the associated shortfall dashboards are currently <strong>disabled</strong> in your tenant rules. To activate this guide's workflow, head to <strong>Settings</strong> &rarr; <strong>Rules Configuration</strong> and toggle <strong>Enable Form 8027 Shortfall Tracking</strong> to "ON".
                  </div>
                </div>
              )}

              <p className="text-gray-700 text-xs leading-relaxed">
                Hospitality businesses filing IRS Form 8027 must ensure reported tips meet an 8% threshold of gross receipts. If they do not, the employer must distribute the shortfall.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-150 space-y-3 mt-2 text-gray-600">
                <span className="font-black text-[10px] uppercase tracking-wider text-purple-700 block">How-To Steps:</span>
                <ul className="list-decimal pl-4 space-y-2">
                  <li>
                    <strong>Navigate to Dashboard:</strong> Click on **"Check IRS 8% Threshold"** in your left navigation sidebar menu.
                  </li>
                  <li>
                    <strong>Define Audit Range:</strong> Click inside the **Start Date** date box and select your start date. Click inside **End Date** and select your end date. Click the blue **Run Compliance Audit** button.
                  </li>
                  <li>
                    <strong>Review compliance:</strong> Check the audit card display. It compares your total **Gross Receipts** against total **Reported Tips** over your specified date range.
                  </li>
                  <li>
                    <strong>Assess Compliance Status:</strong> If reported tips are over 8% of gross sales, you will see a green **"Compliant"** status bar. If reported tips are under 8% of gross sales, the dashboard displays a warning message stating the calculated **Compliance Shortfall** amount (the money missing to meet the 8% target).
                  </li>
                  <li>
                    <strong>Select Shortfall Distribution Method:</strong> If a shortfall is present, scroll down to the allocation section. Select one of the three IRS-approved shortfall allocation methods from the dropdown menu:
                    <ul className="list-disc pl-4 mt-1 space-y-1 text-gray-500">
                      <li><strong>Hours Worked Method:</strong> Shortfall is allocated proportionally based on each employee's share of total working hours during the period.</li>
                      <li><strong>Gross Receipts Method:</strong> Allocated based on each employee's share of total restaurant sales.</li>
                      <li><strong>Good Faith Agreement:</strong> Custom equal or mutually agreed-upon ratio distribution.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Review Distribution Preview:</strong> Click the **Distribute Shortfall** button. The system displays a preview list showing each eligible employee's name, their hours/sales ratio, and their calculated share of the shortfall.
                    </li>
                  <li>
                    <strong>Save Allocation:</strong> Click the green **Save Shortfall Allocation Ledger** button at the bottom of the table. This writes the records to the isolated **shortfall_allocations** ledger in Firestore, keeping a persistent history that you can view, export, or print under the **Shortfall History Ledger** section below.
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* ==================== 4. SUPERADMIN SECTION ==================== */}

      </div>

      {/* Printing Styles Injection */}
      <style>{`
        @media print {
          body {
            background-color: white !important;
            color: black !important;
            font-size: 12px !important;
          }
          .no-print {
            display: none !important;
          }
          .page-break-after {
            page-break-after: always;
          }
          .card, .bg-white {
            border: 1px solid #e5e7eb !important;
            box-shadow: none !important;
            background-color: transparent !important;
          }
        }
      `}</style>

      {showWalkthrough && (
        <InteractiveWalkthrough onClose={() => setShowWalkthrough(false)} />
      )}
    </div>
  );
};
